import { doc, updateDoc } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  addNewMessage,
  createConversationThread,
  getDb,
  getLastMessage,
  getMessages,
} from "../../services/FirebaseStorage";
import {
  AuthService,
  NotificationService,
  RatingReviewService,
} from "../../services";
import { toast } from "react-toastify";
import { formatDateENGB } from "../../utils/common-methods";
import { injectModels } from "../../redux/injectModels";
import { Form } from "react-bootstrap";
import { globalConstant } from "../../constants/globalConstant";

const MessageBox = (props) => {
  const location = useLocation();
  let loginUser = AuthService.getLoginUser();
  const [messages, setMessages] = useState([]);

  var [isPreEventMessage, setPreEventMessage] = useState(false);
  var [eventName, setEventName] = useState("");

  var [message, setMessage] = useState("");
  const [messageError, setMessageError] = useState("");
  const [isLoading, setLoading] = useState(false);
  var todayDate = new Date();
  todayDate = todayDate.setHours(0, 0, 0, 0);
  const thread = location.state?.thread || null;
  const threadId = thread?.id || null;
  const isThreadExist = location.state?.isThreadExist || false;

  // Safe navigation helper
  const safeGet = (obj, path, defaultValue = null) => {
    try {
      return (
        path.split(".").reduce((acc, part) => acc?.[part], obj) ?? defaultValue
      );
    } catch (error) {
      console.error("Safe navigation error:", error);
      return defaultValue;
    }
  };

  useEffect(() => {
    setLoading(true);
  }, [threadId]);

  const addDefaultImg = (e) => {
    if (e && e.target) {
      e.target.onerror = null;
      e.target.src = "/assets/images/users/user-dummy-img.jpg";
    }
  };

  //handle message listner
  useEffect(() => {
    let unsubscribe;
    const bootstrapAsync = async () => {
      try {
        await getBusinessProfile();

        const db = await getDb();
        if (!db) {
          console.error("Database connection failed");
          setLoading(false);
          return;
        }

        if (location.state?.isPreEventMessage) {
          setPreEventMessage(true);
          setEventName(location.state?.eventName || "");
        }

        // Validate threadId exists
        if (!threadId) {
          console.error("Thread ID is missing");
          setLoading(false);
          return;
        }

        const messagesData = await getLastMessage(threadId);

        if (messagesData && messagesData?.length > 0 && loginUser) {
          const lastMessage = messagesData[0];
          const lastReadTimestamp = safeGet(
            thread,
            `lastRead.${loginUser?.id}`,
          );

          if (
            thread?.lastRead &&
            lastMessage?.userId !== loginUser?.id &&
            lastMessage?.created !== lastReadTimestamp
          ) {
            // update thread
            let lastRead = {};
            const threadRef = await doc(db, `swallow_chat`, `${threadId}`);

            if (threadRef) {
              lastRead[loginUser?.id] = lastMessage?.created || new Date();
              lastRead[lastMessage?.userId] =
                lastMessage?.created || new Date();
              try {
                await updateDoc(threadRef, {
                  lastRead: lastRead,
                });
              } catch (e) {
                console.error("ERROR_boot:updateDoc ", e);
              }
            }
          }
        }

        unsubscribe = await getMessages(threadId, async (messageData) => {
          setLoading(false);
          if (messageData && Array.isArray(messageData)) {
            addDateSpearationForMessages(messageData);
          }
        });
      } catch (e) {
        console.error("ERROR_boot: ", e);
        setLoading(false);
      }
    };

    if (threadId) {
      bootstrapAsync();
    } else {
      setLoading(false);
      console.warn("No thread ID available, skipping bootstrap");
    }

    return () => {
      if (unsubscribe && typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [threadId]);

  const getBusinessProfile = async () => {
    try {
      const db = await getDb();
      if (!db) {
        console.error("Database connection failed");
        return null;
      }

      if (safeGet(location, "state.thread.createdBy") && loginUser) {
        const id =
          thread?.createdBy === loginUser?.id
            ? thread?.toUser
            : thread?.createdBy;

        if (!id) {
          console.warn("No user ID found for business profile");
          return null;
        }

        const res = await RatingReviewService.getBusinessProfileById(id);
        if (res?.isSuccess && res?.data) {
          const avatarPath = res?.data?.businessLogoUrl || "";

          const threadRef = await doc(db, `swallow_chat`, `${threadId}`);

          if (threadRef) {
            try {
              if (thread?.createdBy === loginUser?.id) {
                await updateDoc(threadRef, {
                  toAvatar: avatarPath,
                });
              } else {
                await updateDoc(threadRef, {
                  avatar: avatarPath,
                });
              }
            } catch (e) {
              console.error("Error updating avatar:", e);
            }
          }

          return res?.data;
        }
      }
      return null;
    } catch (error) {
      console.error("Error in getBusinessProfile:", error);
      return null;
    }
  };

  const handleMessageChange = (e) => {
    e.preventDefault();
    if (!e || !e.target) return;

    const val = e.target.value;
    if (val?.length > 0) {
      setMessageError("");
    }
    setMessage(val || "");
  };

  const addDateSpearationForMessages = (messageData) => {
    if (
      !messageData ||
      !Array.isArray(messageData) ||
      messageData.length === 0
    ) {
      setMessages([]);
      return;
    }

    var lastCompareDate = null;
    var newMessages = [];

    messageData.forEach((message) => {
      if (!message || !message?.created) return;

      try {
        var newDate = message?.created?.toDate();
        if (!newDate) return;

        newDate = newDate.setHours(0, 0, 0, 0);

        if (lastCompareDate == null) {
          lastCompareDate = newDate;
          newMessages.push({
            message: `${formatDateENGB(message?.created?.toDate())}`,
            type: "divider",
            date: lastCompareDate,
          });
        }

        if (lastCompareDate !== newDate) {
          newMessages.push({
            message: `${formatDateENGB(message?.created?.toDate())}`,
            type: "divider",
            date: newDate,
          });
        }

        newMessages.push(message);
        lastCompareDate = newDate;
      } catch (error) {
        console.error("Error processing message date:", error);
      }
    });

    setMessages(newMessages);
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!loginUser) {
      toast.error("Session expired. Please login again.");        
      return;
    }

    if (!message || !message.trim()) {
      setMessageError("Please enter some text");
      return;
    }

    if (
      globalConstant.emailRegex?.test(message) ||
      globalConstant.phoneRegex?.test(message)
    ) {
      setMessageError("Phone numbers & email are not allowed");
      return;
    } else {
      setMessageError("");
    }

    const finalMessage = isPreEventMessage
      ? `(${eventName || ""}) ${message}`
      : message;

    const lastRead = {};
    const currentTime = new Date();
    const messageObj = {
      message: finalMessage,
      created: currentTime,
      userId: loginUser?.id,
    };

    setMessage("");

    try {
      if (!isThreadExist) {
        // Validate required fields
        if (!thread?.toUser) {
          toast.error("Invalid thread configuration");
          return;
        }

        lastRead[loginUser?.id] = currentTime;
        lastRead[thread?.toUser] = new Date(currentTime.getTime() - 1000 * 60);

        const threadObj = {
          avatar: thread?.avatar || "",
          toAvatar: thread?.toAvatar || "",
          created: currentTime,
          createdBy: loginUser?.id,
          title: thread?.title || "",
          fromName: loginUser?.firstName || "",
          toName: thread?.title || "",
          toUser: thread?.toUser,
          lastRead: lastRead,
        };

        await createConversationThread(threadId, threadObj);
      } else {
        // update read timestamp for current user
        lastRead[loginUser?.id] = currentTime;

        const otherUserId =
          loginUser?.id === thread?.toUser ? thread?.createdBy : thread?.toUser;

        lastRead[otherUserId] = safeGet(
          thread,
          `lastRead.${otherUserId}`,
          currentTime,
        );

        await updateThread(lastRead, currentTime);
      }

      const newMessages = [...messages];
      newMessages.push(messageObj);
      setMessages(newMessages);

      const docRef = await addNewMessage(threadId, messageObj);
      if (!docRef || docRef.id == null) {
        toast.error("Error while sending message");
      } else {
        const recipientId =
          loginUser?.id === thread?.toUser ? thread?.createdBy : thread?.toUser;

        const recipientName =
          loginUser?.id === thread?.toUser ? thread?.toName : thread?.fromName;

        if (recipientId && finalMessage && recipientName) {
          sendEmailNotification(recipientId, finalMessage, recipientName);
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message. Please try again.");
    }
  };

  const updateThread = async (lastRead, currentTime) => {
    if (!lastRead || !currentTime) {
      console.error("Missing required parameters for updateThread");
      return;
    }

    try {
      const db = await getDb();
      if (!db) {
        console.error("Database connection failed");
        return;
      }

      if (!threadId) {
        console.error("Thread ID is missing");
        return;
      }

      const threadRef = await doc(db, `swallow_chat`, `${threadId}`);

      if (threadRef) {
        await updateDoc(threadRef, {
          lastRead: lastRead,
        });
      }
    } catch (e) {
      console.error("Error updating thread:", e);
    }
  };

  const sendEmailNotification = async (user, message, title) => {
    if (!user || !message || !title) {
      console.warn("Missing parameters for email notification");
      return;
    }

    try {
      const params = {
        to: [user],
        message: message,
        title: title,
        isGroup: false,
      };
      await NotificationService.sendEmailNotification(params);
    } catch (error) {
      console.error("Error sending email notification:", error);
    }
  };

  const getAvatar = () => {
    if (!thread || !loginUser) {
      return "";
    }

    return thread?.createdBy === loginUser?.id
      ? thread?.toAvatar || ""
      : thread?.avatar || "";
  };

  // Early return if no thread exists
  if (!threadId && !isLoading) {
    return (
      <div className="text-center p-4">
        <p>No conversation selected</p>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="modal-dialog-scrollable">
        <div className="modal-content">
          <div className="msg-head">
            <div className="row">
              <div className="col-8">
                <div className="d-flex align-items-center">
                  <span className="chat-icon">
                    <img
                      className="img-fluid"
                      src="https://mehedihtml.com/chatbox/assets/img/arroleftt.svg"
                      alt="title"
                    />
                  </span>
                  <div className="flex-shrink-0">
                    <img
                      style={{
                        width: 50,
                        height: 50,
                        borderRadius: 25,
                        border: "2px solid gray",
                      }}
                      className="img-fluid"
                      src={getAvatar()}
                      alt="user img"
                      onError={addDefaultImg}
                    />
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h3>
                      {thread?.createdBy === loginUser?.id
                        ? thread?.toName || "User"
                        : thread?.fromName || "User"}
                    </h3>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-body bottom-scroll">
            <div id="messageContainer" className="bottom-scroll">
              {isLoading ? (
                <h4 style={{ textAlign: "center" }}>Loading...</h4>
              ) : null}

              {messages?.length > 0 && !isLoading ? (
                <div className="msg-body">
                  <ul>
                    {messages.map((message, index) => (
                      <React.Fragment key={index}>
                        {message?.type === "divider" ? (
                          <li>
                            <div className="divider">
                              <h6>
                                {todayDate === message?.date
                                  ? "Today"
                                  : message?.message || ""}
                              </h6>
                            </div>
                          </li>
                        ) : (
                          <li
                            className={
                              message?.userId === loginUser?.id
                                ? "repaly"
                                : "sender"
                            }
                          >
                            <p>{message?.message || ""}</p>
                            <span className="time">
                              {message?.timeString || ""}
                            </span>
                          </li>
                        )}
                      </React.Fragment>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </div>

          <div className="send-box">
            <form onSubmit={onSubmit}>
              <Form.Control
                className="form-control"
                aria-label="message…"
                placeholder="Write message…"
                as="textarea"
                rows={1}
                style={{ maxHeight: 100 }}
                value={message}
                onChange={handleMessageChange}
              />
              <button type="button" onClick={onSubmit}>
                <i className="fa fa-paper-plane" aria-hidden="true"></i> Send
              </button>
            </form>
            {messageError && <p className="text-danger">{messageError}</p>}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default injectModels(["application"])(MessageBox);
